import { AddAppDialog } from '@/components/shell/add-app-dialog';
import { RevealKeyDialog } from '@/components/shell/reveal-key-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useContextTree } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown, Layers3, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

/** Compact project context for the sidebar. Workspace selection follows the chosen app internally,
 * keeping the visible controls focused on the organization, app, and delivery environment. */
export function ContextSwitcher({ onNavigate }: { onNavigate?: () => void }): React.JSX.Element {
  const { data: orgs, isLoading, isFetching } = useContextTree();
  const ctx = useContextStore();
  const navigate = useNavigate();
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const org = orgs?.find((o) => o.id === ctx.orgId) ?? orgs?.[0];
  const workspace = org?.workspaces.find((w) => w.id === ctx.workspaceId) ?? org?.workspaces[0];
  const app = workspace?.apps.find((a) => a.id === ctx.appId) ?? workspace?.apps[0];
  const environment =
    app?.environments.find((e) => e.id === ctx.environmentId) ??
    app?.environments.find((e) => e.name === 'production') ??
    app?.environments[0];

  // Backfill the store from the tree. The store starts empty (and can hold a stale id after a
  // resource is deleted elsewhere), so whatever the cascade above actually resolved to becomes the
  // truth. Without this every page's `enabled: Boolean(appId)` query would sit disabled forever.
  const { setOrg, setWorkspace, setApp, setEnvironment } = ctx;
  useEffect(() => {
    if (org && org.id !== ctx.orgId) setOrg(org.id);
    if (workspace && workspace.id !== ctx.workspaceId) setWorkspace(workspace.id);
    // An invalidated context query keeps its old tree while refetching. Do not let that stale tree
    // overwrite an app/environment selected optimistically by create/delete actions.
    if (app && app.id !== ctx.appId && (!ctx.appId || !isFetching)) setApp(app.id);
    if (environment && environment.id !== ctx.environmentId && (!ctx.environmentId || !isFetching)) {
      setEnvironment(environment.id);
    }
  }, [org, workspace, app, environment, ctx, isFetching, setOrg, setWorkspace, setApp, setEnvironment]);

  if (isLoading) return <Skeleton className="h-7 w-64" />;
  if (!org) return <span className="text-sm text-muted-foreground">No organization</span>;

  const apps = org.workspaces.flatMap((candidateWorkspace) =>
    candidateWorkspace.apps.map((candidateApp) => ({ app: candidateApp, workspace: candidateWorkspace }))
  );
  const canCreate = org.role === 'admin' || org.role === 'editor';

  return (
    <div className="rounded-lg border bg-background p-2 shadow-sm">
      <Select value={org.id} onValueChange={setOrg}>
        <SelectTrigger className="h-6 border-0 px-1 text-[11px] font-medium text-muted-foreground shadow-none focus:ring-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {orgs?.map((candidate) => (
            <SelectItem key={candidate.id} value={candidate.id}>
              {candidate.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {app ? (
        <Select
          value={app.id}
          onValueChange={(appId) => {
            const target = apps.find((candidate) => candidate.app.id === appId);
            if (!target) return;
            setWorkspace(target.workspace.id);
            setApp(target.app.id);
          }}
        >
          <SelectTrigger className="mt-1 h-9 border-0 px-1 shadow-none focus:ring-0 [&>svg]:hidden">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="grid size-7 shrink-0 place-items-center rounded-md bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                <Layers3 className="size-3.5" />
              </span>
              <span className="min-w-0 text-left">
                <span className="block truncate text-sm font-medium">{app.name}</span>
                <span className="block truncate text-[10px] text-muted-foreground">iOS app</span>
              </span>
              <ChevronDown className="ml-auto size-3.5 shrink-0 text-muted-foreground" />
            </div>
          </SelectTrigger>
          <SelectContent>
            {apps.map((candidate) => (
              <SelectItem key={candidate.app.id} value={candidate.app.id}>
                {candidate.app.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      {app?.environments.length ? (
        <div className="mt-2 grid grid-cols-2 gap-1 border-t pt-2">
          {app.environments.map((env) => (
            <button
              key={env.id}
              type="button"
              onClick={() => setEnvironment(env.id)}
              className={
                env.id === environment?.id
                  ? 'rounded-md bg-foreground px-2 py-1.5 text-[11px] font-medium capitalize text-background'
                  : 'rounded-md px-2 py-1.5 text-[11px] capitalize text-muted-foreground hover:bg-secondary hover:text-foreground'
              }
            >
              <span className="inline-flex items-center gap-1.5">
                <span className={`size-1.5 rounded-full ${env.sdk_enabled ? 'bg-emerald-500' : 'bg-red-500'}`} />
                {env.name}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {canCreate ? (
        <AddAppDialog
          onCreated={(result) => setRevealedKey(result.write_key)}
          trigger={
            <button
              type="button"
              className="mt-1.5 flex w-full items-center gap-1.5 rounded-md px-1 py-1 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <Plus className="size-3" />
              Add app
            </button>
          }
        />
      ) : (
        <button
          type="button"
          disabled
          title="Ask an admin or editor to add an app"
          className="mt-1.5 flex w-full cursor-not-allowed items-center gap-1.5 rounded-md px-1 py-1 text-[11px] text-muted-foreground opacity-60"
        >
          <Plus className="size-3" />
          Add app
        </button>
      )}

      <RevealKeyDialog
        value={revealedKey}
        title="Copy your SDK key now"
        onClose={() => {
          setRevealedKey(null);
          onNavigate?.();
          void navigate({ to: '/install' });
        }}
      />
    </div>
  );
}
